import { BaseViewStore } from 'core/stores';
import { action, makeObservable, observable } from 'mobx';
import { hasLabel, isOnePerBox, isSuperAdminOrganization, notificationError } from 'core/utils';
import FulfillmentOrderProcessingStore from './FulfillmentOrderProcessingStore';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import { sourceOfChange } from 'common/constants';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

class SplitFulfillmentOrderViewStore extends BaseViewStore {
	fulfillmentOrder = null;
	fulfillmentOrdersInSplit = null;
	originalFulfillmentOrderId = null;

	isEdit;
	dragAndDropField = 'sku';
	@observable initialFulfillmentOrderDropBox = {};
	@observable dropBoxList = [];

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.isEdit = modalParams.data.isEdit;
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.fulfillmentOrderProcessingStore = new FulfillmentOrderProcessingStore(rootStore);
		this.initialize(modalParams.data.selectedItemId);
	}

	@action.bound
	initialize = async selectedItemId => {
		this.suspend();
		if (this.isEdit) {
			await this.findFulfillmentOrders(selectedItemId, this.modalParams.data.orderId);
		} else {
			await this.getFulfillmentOrder(selectedItemId);
		}
		this.resume();
	};

	@action.bound
	getFulfillmentOrder = async selectedItemId => {
		this.fulfillmentOrder = await this.fulfillmentOrderStore.getFulfillmentOrder(selectedItemId, {
			embed: ['fulfillmentOrderItems'],
		});
		this.setIntialFulfillmentOrderDropBox();
		this.addNewFulfillmentOrder();
	};

	@action.bound
	findFulfillmentOrders = async (selectedItemId, marketplaceOrderId) => {
		this.fulfillmentOrdersInSplit = (await this.fulfillmentOrderStore.findFulfillmentOrder({
			page: 1,
			rpp: 100,
			orderId: marketplaceOrderId,
			embed: ['fulfillmentOrderStatus', 'fulfillmentOrderItems', 'fulfillmentOrderItems.product', 'shipmentInfos'],
		})).item.sort((firstItem, secondItem) => firstItem.fulfillmentOrderId - secondItem.fulfillmentOrderId);

		this.fulfillmentOrder = _.find(this.fulfillmentOrdersInSplit, fulfillmentOrderInSplit => {
			return fulfillmentOrderInSplit.id == selectedItemId;
		});
		this.setIntialFulfillmentOrderDropBox();
		this.setFulfillmentOrderInSplitDropBoxes(selectedItemId);
	};

	setFulfillmentOrderInSplitDropBoxes = initialFulfillmentOrderId => {
		_.forEach(this.fulfillmentOrdersInSplit, fulfillmentOrderInSplit => {
			if (fulfillmentOrderInSplit.id != initialFulfillmentOrderId) {
				this.dropBoxList.push({
					id: fulfillmentOrderInSplit.id,
					fulfillmentOrderId: fulfillmentOrderInSplit.fulfillmentOrderId,
					onePerBox: isOnePerBox(fulfillmentOrderInSplit),
					hasLabel: hasLabel(fulfillmentOrderInSplit),
					items: _.map(fulfillmentOrderInSplit.fulfillmentOrderItems, item => {
						return { id: item.id, sku: item.sku, quantity: item.quantity, key: uuidv4() };
					}),
					key: uuidv4(),
				});
			}
		});
	};

	setIntialFulfillmentOrderDropBox = () => {
		this.initialFulfillmentOrderDropBox = {
			id: this.fulfillmentOrder.id,
			items: _.map(this.fulfillmentOrder.fulfillmentOrderItems, item => {
				return { id: item.id, sku: item.sku, quantity: item.quantity, key: uuidv4() };
			}),
			isInitial: true,
			key: uuidv4(),
		};
		if (this.isEdit) {
			if (this.fulfillmentOrder.isSplit) {
				this.originalFulfillmentOrderId = this.fulfillmentOrder.id;
			} else {
				this.originalFulfillmentOrderId = this.fulfillmentOrder.originalFulfillmentOrderId;
			}
		}
	};

	validateFulfillmentOrders = () => {
		const invalidItem = _.find(this.dropBoxList, dropBox => {
			return this.isEdit ? dropBox.items.length == 0 && dropBox.id === '' : dropBox.items.length == 0;
		});
		if (invalidItem != null) {
			this.rootStore.notificationStore.error('SPLITFULFILLMENTORDERMODAL.NOTIFICATION.INVALIDFULFILLMENTORDERS');
		} else {
			this.splitFulfillmentOrder();
		}
	};

	@action.bound
	splitFulfillmentOrder = async () => {
		this.suspend();
		try {
			if (!this.modalParams.data.isEdit) {
				await this.splitFulfillmentOrderInitial();
			} else {
				await this.splitFulfillmentOrderEdit();
			}

			this.rootStore.notificationStore.success([
				this.isEdit
					? 'SPLITFULFILLMENTORDERMODAL.NOTIFICATION.SUCCESSEDIT'
					: 'SPLITFULFILLMENTORDERMODAL.NOTIFICATION.SUCCESS',
				{ fulfillmentOrderId: this.fulfillmentOrder.fulfillmentOrderId },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
		this.modalParams.success();
	};

	splitFulfillmentOrderInitial = async () => {
		const fulfillmentOrders = _.map(this.dropBoxList, dropBox => {
			return { id: dropBox.id, fulfillmentOrderItems: dropBox.items };
		});

		fulfillmentOrders.push({
			id: this.initialFulfillmentOrderDropBox.id,
			fulfillmentOrderItems: this.initialFulfillmentOrderDropBox.items,
		});

		await this.fulfillmentOrderProcessingStore.splitFulfillmentOrder(
			fulfillmentOrders,
			this.isSuperAdminOrganization
				? sourceOfChange.splitFulfillmentOrderSuperAdmin
				: sourceOfChange.splitFulfillmentOrderSeller
		);
	};

	splitFulfillmentOrderEdit = async () => {
		const fulfillmentOrders = [];
		const fulfillmentOrdersOnePerBoxAndLabel = [];
		_.forEach(this.dropBoxList, dropBox => {
			if (!dropBox.onePerBox && !dropBox.hasLabel) {
				fulfillmentOrders.push({ id: dropBox.id, fulfillmentOrderItems: dropBox.items });
			} else {
				fulfillmentOrdersOnePerBoxAndLabel.push({ id: dropBox.id });
			}
		});

		// If whole FO dropbox is deleted by x button
		_.forEach(this.fulfillmentOrdersInSplit, fulfillmentOrder => {
			// If order has label or it's one per box it shouldn't be splittable
			const notSplittableOrder =
				_.findIndex(fulfillmentOrdersOnePerBoxAndLabel, fo => {
					return fo.id === fulfillmentOrder.id;
				}) != -1;

			if (
				fulfillmentOrder.id != this.originalFulfillmentOrderId &&
				fulfillmentOrder.id != this.fulfillmentOrder.id &&
				!notSplittableOrder
			) {
				const isDropBoxDeleted =
					_.findIndex(this.dropBoxList, dropBox => {
						return dropBox.id == fulfillmentOrder.id && dropBox.id != '';
					}) == -1;
				if (isDropBoxDeleted) {
					fulfillmentOrders.push({ id: fulfillmentOrder.id, fulfillmentOrderItems: [] });
				}
			}
		});

		fulfillmentOrders.push({
			id: this.initialFulfillmentOrderDropBox.id,
			isMainFulfillmentOrder: true,
			fulfillmentOrderItems: this.initialFulfillmentOrderDropBox.items,
		});

		await this.fulfillmentOrderProcessingStore.editFulfillmentOrderSplit(
			fulfillmentOrders,
			this.isSuperAdminOrganization
				? sourceOfChange.editFulfillmentOrderSplitSuperAdmin
				: sourceOfChange.editFulfillmentOrderSplitSeller
		);
	};

	@action.bound
	addNewFulfillmentOrder = () => {
		this.dropBoxList.push({ id: '', items: [], key: uuidv4() });
	};

	@action.bound
	onFulfillmentOrderDelete = (dropBoxToDelete, index) => {
		if (this.isEdit && this.originalFulfillmentOrderId == dropBoxToDelete.id) {
			this.rootStore.notificationStore.error('SPLITFULFILLMENTORDERMODAL.NOTIFICATION.ORIGINALFODELETE');
			return;
		}
		if (dropBoxToDelete.items.length > 0) {
			_.each(dropBoxToDelete.items, itemToDelete => {
				const existingItem = _.find(this.initialFulfillmentOrderDropBox.items, item => {
					return itemToDelete.sku == item.sku;
				});
				if (existingItem != null) {
					existingItem.quantity += itemToDelete.quantity;
				} else {
					this.initialFulfillmentOrderDropBox.items.push(itemToDelete);
				}
			});
		}
		this.dropBoxList.splice(index, 1);
	};

	@action.bound
	onDropCallback = (sku, newDropBox, currentDropBox) => {
		const matchingItem = _.find(currentDropBox.items, fulfillmentOrderItem => {
			return fulfillmentOrderItem.sku == sku;
		});
		if (currentDropBox.isInitial && currentDropBox.items.length == 1 && currentDropBox.items[0].quantity == 1) {
			if (!this.isEdit) {
				this.rootStore.notificationStore.error('SPLITFULFILLMENTORDERMODAL.NOTIFICATION.NOITEMSREMAINING');
			} else {
				this.rootStore.notificationStore.error('SPLITFULFILLMENTORDERMODAL.NOTIFICATION.NOITEMSREMAININGEDIT');
			}
		} else if (
			this.isEdit &&
			currentDropBox.id == this.originalFulfillmentOrderId &&
			currentDropBox.items.length == 1 &&
			currentDropBox.items[0].quantity == 1
		) {
			this.rootStore.notificationStore.error('SPLITFULFILLMENTORDERMODAL.NOTIFICATION.NOITEMSREMAINING');
		} else if (matchingItem != null) {
			matchingItem.quantity = matchingItem.quantity - 1;
			const existingItem = _.find(newDropBox.items, fulfillmentOrderItem => {
				return fulfillmentOrderItem.sku === sku;
			});
			if (existingItem != null) {
				existingItem.quantity = existingItem.quantity + 1;
			} else {
				newDropBox.items.push({ id: matchingItem.id, sku: sku, quantity: 1, key: uuidv4() });
			}
			_.remove(currentDropBox.items, fulfillmentOrderItem => {
				return fulfillmentOrderItem.quantity === 0;
			});
		}
	};

	@action.bound
	onCanDropCallback(newDropBox, currentDropBox) {
		if (!this.isEdit) {
			return true;
		}
		return (newDropBox.isInitial || currentDropBox.isInitial) && !newDropBox.hasLabel && !newDropBox.onePerBox;
	}

	@action.bound
	onCanDragCallback(currentDropBox) {
		if (!this.isEdit) {
			return true;
		}
		return !currentDropBox.hasLabel && !currentDropBox.onePerBox;
	}
}

export default SplitFulfillmentOrderViewStore;
